<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import { onDestroy, onMount, tick } from 'svelte';
	import localizedFormat from 'dayjs/plugin/localizedFormat';
	// Stores
	import ACLStore from '@/Stores/ACLStore.js';
	import AuthUserStore from '@/Stores/AuthUserStore.js';
	import CampaignStore from '@/Stores/CampaignStore.js';
	// Constants
	import { PromotionsType, PromotionsTypeNames } from '@/Constants/Promotions.js';
	import { CampaignStatus, CampaignStatusNames } from '@/Constants/Campaigns.js';
	import { Countries, CountryNames } from '@/Constants/Country.js';
	import { UserRatingName } from '@/Constants/User.js';
	// Components
	import Helmet from '@/Components/Helmet.svelte';
	import Pagination from '@/Components/Pagination.svelte';
	import PillMultiSelect from '@/Components/PillMultiSelect.svelte';
	import Datepicker from '@/Components/Datepicker.svelte';
	import SelectInput from '@/Components/SelectInput.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import TextInput from '@/Components/TextInput.svelte';
	import Label from '@/Components/Label.svelte';
	import Icon from '@/Components/Icon.svelte';
	import Prompt from '@/Components/Prompt.svelte';
	import Polling from '@/Components/Polling.svelte';
	// import CheckBox from '@/Components/CheckBox.svelte';
	// Partials
	import CreateCampaign from '@/Partials/Campaigns/_Create.svelte';
	import UpdateCampaign from '@/Partials/Campaigns/_Update.svelte';
	import ViewCampaign from '@/Partials/Campaigns/_Show.svelte';
	import ViewCampaignUsers from '@/Partials/Campaigns/_ViewCampaignUsers.svelte';
	// Services
	import { getUrlParameter, paginationBuilder, setUrlParameter } from '@/Services/Helpers.js';
	import { notify } from '@/Services/Notify.js';
	import Sockets from '@/Services/Sockets.js';

	dayjs.extend(localizedFormat);

	let page = 1;
	let updateCampaignId;
	let viewCampaignId;
	let viewCampaignUsersCampaignId;
	let showViewCampaignUsers = false;
	let showCreateCampaign = false;
	let showUpdateCampaign = false;
	let showViewCampaign = false;
	let errors = {};
	let startDate = dayjs().format('YYYY-MM-DD');
	let campaignCreateType;
	// let selectedCampaigns = [];
	let closeCampaignId;
	let promptCloseCampaign = false;

	$: page = parseInt(getUrlParameter('page')) || 1; // Default to page 1!
	$: page && page !== 1 && setUrlParameter('page', page);
	$: links = $CampaignStore.count() && paginationBuilder(CampaignStore.getPagination()); //Used for pagination component
	$: numberOfPages =
		$CampaignStore.count() && CampaignStore.getPagination() && CampaignStore.getPagination().total_pages;
	$: campaigns = ($CampaignStore && filterCampaigns()) ?? [];

	function changePage(e) {
		if (e) {
			page = e.detail.page;
			getCampaigns();
			setUrlParameter('page', page);
		}
	}

	export function getCampaigns() {
		CampaignStore.getCampaigns(false, { perPage: 30, page: page });
	}

	function filterCampaigns() {
		if (!$CampaignStore) {
			return [];
		}

		return $CampaignStore.all();
	}

	getCampaigns();

	let selectedCountry = [false];
	// eslint-disable-next-line no-unused-vars
	$: countryOptions = Object.entries(Countries).map(([key, value]) => [value, CountryNames[value]]);

	let selectedRewardType = [false];
	// eslint-disable-next-line no-unused-vars
	$: rewardTypeOptions = Object.entries(PromotionsType).map(([key, value]) => [value, PromotionsTypeNames[value]]);

	let selectedStatus = [false];
	// eslint-disable-next-line no-unused-vars
	$: statusOptions = Object.entries(CampaignStatus).map(([key, value]) => [value, CampaignStatusNames[value]]);

	let selectedApproval = [false];
	$: approvalOptions =
		($CampaignStore && CampaignStore.getApprovalManagers() && Object.entries(CampaignStore.getApprovalManagers())) ??
		[];

	async function createNewCampaign() {
		if (campaignCreateType) {
			showCreateCampaign = true;
			await tick();
			window.scrollTo(0, 0);
		} else {
			errors = { createRewardType: ['You must select a reward type to create'] };
		}
	}

	async function editCampaign(campaignId) {
		updateCampaignId = campaignId;
		showUpdateCampaign = true;
		await tick();
		window.scrollTo(0, 0);
	}

	async function viewCampaign(campaignId) {
		viewCampaignId = campaignId;
		showViewCampaign = true;
		await tick();
		window.scrollTo(0, 0);
	}

	async function viewCampaignUsers(campaignId) {
		viewCampaignUsersCampaignId = campaignId;
		showViewCampaignUsers = true;
		await tick();
		window.scrollTo(0, 0);
	}

	async function closeCampaign(campaignId) {
		closeCampaignId = campaignId;
		promptCloseCampaign = true;
	}
	async function onCloseCampaign() {
		if (!closeCampaignId) {
			return;
		}
		promptCloseCampaign = false;
		CampaignStore.close(closeCampaignId)
			.then(() => {
				notify({
					type: 'success',
					title: 'Campaign removed',
					message: 'Campaign successfully removed!',
					timeout: 3
				});
				getCampaigns();
			})
			.catch((e) => {
				errors = e;
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Problem removing campaign',
					timeout: 3
				});
			});
		closeCampaignId = undefined;
	}

	/*	function updateMultiSelected(event, campaignId) {
		if (event.target.checked) {
			selectedCampaigns = [...selectedCampaigns, campaignId];
			return;
		}

		selectedCampaigns = selectedCampaigns.filter((selectedCampaign) => {
			return selectedCampaign !== campaignId;
		});
	}*/

	const refreshCampaigns = () => {
		getCampaigns();
	};

	const approveCampaign = (campaignId) => {
		CampaignStore.approve(campaignId)
			.then(() => {
				notify({
					type: 'success',
					title: 'Campaign approved',
					message: 'Campaign successfully approved!',
					timeout: 3
				});
				getCampaigns();
			})
			.catch((e) => {
				errors = e;
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Problem approving campaign',
					timeout: 3
				});
			});
	};
	const declineCampaign = (campaignId) => {
		CampaignStore.decline(campaignId)
			.then(() => {
				notify({
					type: 'success',
					title: 'Campaign declined',
					message: 'Campaign successfully declined!',
					timeout: 3
				});
				getCampaigns();
			})
			.catch((e) => {
				errors = e;
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Problem declining campaign',
					timeout: 3
				});
			});
	};

	function getUserRatingList(userRatingsIds) {
		let userRatingString = '';
		for (const userRatingId of userRatingsIds) {
			if (userRatingString !== '') {
				userRatingString = userRatingString + ', ';
			}
			userRatingString = userRatingString + UserRatingName[userRatingId];
		}
		return userRatingString;
	}

	onMount(() => {
		Sockets.subscribeToCampaigns();
	});

	onDestroy(() => {
		Sockets.unsubscribeFromCampaigns();
	});
</script>

<Helmet title="Campaigns Dashboard" />

<Polling pollFunction={getCampaigns} frequency={60} />

{#if $ACLStore && ACLStore.granted('campaigns.create') && showCreateCampaign && campaignCreateType}
	<CreateCampaign
		bind:open={showCreateCampaign}
		type={campaignCreateType}
		approval_managers={CampaignStore.getApprovalManagers()}
		account_managers={CampaignStore.getAccountManagers()}
		on:created={refreshCampaigns}
	/>
{/if}
{#if $ACLStore && ACLStore.granted('campaigns.update') && showUpdateCampaign && updateCampaignId}
	<UpdateCampaign
		bind:open={showUpdateCampaign}
		bind:campaignId={updateCampaignId}
		approval_managers={CampaignStore.getApprovalManagers()}
		account_managers={CampaignStore.getAccountManagers()}
		on:updated={refreshCampaigns}
	/>
{/if}
{#if $ACLStore && ACLStore.granted('campaigns.read') && showViewCampaign && viewCampaignId}
	<ViewCampaign
		bind:open={showViewCampaign}
		bind:campaignId={viewCampaignId}
		account_managers={CampaignStore.getAccountManagers()}
	/>
{/if}
{#if $ACLStore && ACLStore.granted('campaigns.read') && showViewCampaignUsers && viewCampaignUsersCampaignId}
	<ViewCampaignUsers bind:open={showViewCampaignUsers} bind:campaignId={viewCampaignUsersCampaignId} />
{/if}

{#if closeCampaignId && promptCloseCampaign}
	<Prompt
		bind:open={promptCloseCampaign}
		title="Confirm close"
		message="Are you sure you want to close this campaign?"
		onConfirm={() => onCloseCampaign()}
	/>
{/if}

<div>
	<div>
		<!-- Page Header -->
		<div class="flex flex-col items-center w-full p-4 mb-4 text-sm bg-white border-b md:py-2">
			<div class="flex items-center justify-between w-full">
				<div class="flex-1 text-left">
					<h1 class="text-xl font-bold">Campaign dashboard</h1>
				</div>
				<div class="flex flex-row pl-2 w-1/3 hidden">
					<TextInput name="Search" disabled className=" w-full" placeholder="Search" />
					<LoadingButton
						on:click={createNewCampaign}
						disabled
						className="btn btn-black flex justify-center items-center px-4 ml-4"
						>Search
					</LoadingButton>
				</div>
			</div>
		</div>
		<!-- End Page Header -->
		<!-- Page -->
		<div class="p-2 mx-4 bg-gray-100 rounded">
			<div class="flex flex-col flex-grow">
				<div class="flex flex-row space-x-2 justify-end">
					<div class="flex flex-col border-r pr-2 border-black hidden">
						<div class="flex flex-row pb-1">Filters:</div>
						<div class="flex flex-row">
							<Datepicker
								selected={startDate}
								disabled
								displayFormat="DD-MM-YYYY"
								dateChosen={(date) => {
									startDate = dayjs(date).format('YYYY-MM-DD');
								}}
							/>
							<PillMultiSelect
								className="min-w-24"
								name="country"
								label="Country"
								disabled
								singleSelection={true}
								options={[[false, 'All'], ...countryOptions]}
								action=""
								checked={selectedCountry}
								wrapLabel={false}
								on:change={(change) => (selectedCountry = change.detail.checked[0])}
							/>
							<PillMultiSelect
								className="min-w-24"
								name="type"
								label="Reward Type"
								disabled
								singleSelection={true}
								options={[[false, 'All'], ...rewardTypeOptions]}
								action=""
								checked={selectedRewardType}
								on:change={(change) => (selectedRewardType = change.detail.checked[0])}
							/>
							<PillMultiSelect
								className="min-w-24"
								name="status"
								label="Status"
								disabled
								singleSelection={true}
								options={[[false, 'All'], ...statusOptions]}
								action=""
								checked={selectedStatus}
								on:change={(change) => (selectedStatus = change.detail.checked[0])}
							/>
							<PillMultiSelect
								className="min-w-24"
								name="approval"
								label="Approval"
								disabled
								singleSelection={true}
								options={[[false, 'All'], ...approvalOptions]}
								action=""
								checked={selectedApproval}
								on:change={(change) => (selectedApproval = change.detail.checked[0])}
							/>
						</div>
					</div>
					{#if $ACLStore && ACLStore.granted('campaigns.create')}
						<div class="flex flex-col">
							<div class="flex flex-row pb-1 ml-2">Create:</div>
							<div class="flex flex-row pl-2">
								<SelectInput
									name="createRewardType"
									bind:value={campaignCreateType}
									errors={errors && errors.createRewardType}
									on:change={() => {
										errors = {};
									}}
								>
									<option value="" selected disabled>Select campaign type to create</option>
									{#each Object.values(PromotionsType) as promotionTypeId}
										<option value={promotionTypeId}>{PromotionsTypeNames[promotionTypeId]}</option>
									{/each}
								</SelectInput>
								<LoadingButton
									on:click={createNewCampaign}
									className="btn btn-black flex justify-center items-center bg-secondary px-4 ml-4"
									>Create
								</LoadingButton>
							</div>
						</div>
					{/if}
					<div class="flex flex-col hidden">
						<div class="flex flex-row pb-1 ml-3">Multi-select options</div>
						<div class="flex flex-row w-full border-l border-black h-10 mt-px">
							<LoadingButton
								on:click={createNewCampaign}
								disabled
								className="btn btn-black flex justify-center items-center px-4 ml-3"
								>Approve
							</LoadingButton>
							<LoadingButton
								on:click={createNewCampaign}
								disabled
								className="btn btn-black flex justify-center items-center px-4 ml-3"
								>Decline
							</LoadingButton>
							<LoadingButton
								on:click={createNewCampaign}
								disabled
								className="btn btn-black flex justify-center items-center px-4 ml-3"
								>Close
							</LoadingButton>
						</div>
					</div>
				</div>

				{#if numberOfPages > 1}
					<Pagination {links} on:click={changePage} className="my-3" />
				{/if}
				<table class="w-full whitespace-nowrap mt-3">
					<thead>
						<tr class="text-xs text-left text-gray-500 uppercase">
							<!--							<th class="w-px px-2 pt-3 pb-2 font-medium text-left" />-->
							<th class="px-2 pt-3 pb-2 font-medium text-left">ID</th>
							<th class="px-2 pt-3 pb-2 font-medium text-left">Name</th>
							<th class="px-2 pt-3 pb-2 font-medium text-left">Reward type</th>
							<th class="px-2 pt-3 pb-2 font-medium text-left">Campaign period</th>
							<th class="px-2 pt-3 pb-2 font-medium text-left">Audience rating</th>
							<th class="w-px px-2 pt-3 pb-2 font-medium text-center">Approval</th>
							<th class="w-px px-2 pt-3 pb-2 font-medium text-center">Status</th>
							<th class="w-px px-2 pt-3 pb-2 font-medium text-left">Actions</th>
							<th class="w-px pl-2 pt-3 pb-2 font-medium text-left">Manage</th>
						</tr>
					</thead>
					<tbody class="bg-white">
						{#each campaigns as campaign (campaign.id)}
							<tr class="hover:border-t hover:border-b hover:border-b-danger hover:border-t-danger h-10">
								<!--								<td class="w-px px-2 h-10">
									<CheckBox
										checked={selectedCampaigns.includes(campaign.id)}
										on:change={(event) => updateMultiSelected(event, campaign.id)}
									/>
								</td>-->
								<td class="pl-2 pr-6 h-10">
									{campaign.campaign_id}
								</td>
								<td class="px-2 h-10">
									{campaign.name}
								</td>
								<td class="px-2 h-10">
									{PromotionsTypeNames[campaign.type.id]}
								</td>
								<td class="px-2 h-10">
									{dayjs(campaign.publish_date + ' ' + campaign.time_to_start).format('D MMM YYYY, HH:mm')} -
									{dayjs(campaign.end_date + ' ' + campaign.time_to_start).format('D MMM YYYY, HH:mm')}
								</td>
								<td class="px-2 py-1 h-10">
									<div class="flex w-[20rem] max-w-[20rem]">
										<p class="whitespace-break-spaces text-sm">
											{#if 'rating' in campaign.user_criteria}
												{getUserRatingList(campaign.user_criteria.rating.value)}
											{/if}
										</p>
									</div>
								</td>
								<td class="w-px px-2 h-10">
									{#if [CampaignStatus.PENDING, CampaignStatus.SCHEDULED, CampaignStatus.EXPIRED, CampaignStatus.ACTIVE, CampaignStatus.PROCESSING, CampaignStatus.ERROR].includes(campaign.status.id) && campaign.approved_by.id !== undefined}
										<Label
											label={String(campaign.approval_status.name).toLowerCase()}
											classes="uppercase text-white font-semibold px-3 py-1.5"
										/>
									{/if}
								</td>
								<td class="w-px px-2 h-10">
									<Label
										label={String(campaign.status.name).toLowerCase()}
										classes="uppercase font-semibold px-3 py-1.5"
									/>
								</td>
								<td class="w-px px-2 h-10">
									{#if campaign.approval_status.id === CampaignStatus.PENDING && campaign.approved_by.id === $AuthUserStore.id}
										<div class="flex items-center justify-around h-10 space-x-3">
											<button on:click={() => approveCampaign(campaign.id)}>
												<Label
													label={'Approve'}
													classes="uppercase text-white font-semibold hover:bg-green-300 px-3 py-1.5"
												/>
											</button>
											<button on:click={() => declineCampaign(campaign.id)}>
												<Label
													label={'Decline'}
													classes="uppercase text-white font-semibold hover:bg-red-300 px-3 py-1.5"
												/>
											</button>
										</div>
									{/if}
								</td>
								<td class="w-px pl-2 h-10">
									<div class="flex items-center justify-around h-10">
										<button
											title="Open the campaign in view format"
											class="text-gray-800 btn hover:text-blue-500"
											on:click={() => viewCampaign(campaign.id)}
										>
											<Icon name="show" />
										</button>
										<button
											title="Show a list of users who will get this promotion if it were run right now"
											class="text-gray-800 btn hover:text-blue-500"
											on:click={() => viewCampaignUsers(campaign.id)}
										>
											<Icon name="users" />
										</button>
										{#if ![CampaignStatus.CLOSED].includes(campaign.status.id)}
											{#if [CampaignStatus.DRAFT, CampaignStatus.PENDING].includes(campaign.status.id)}
												<button
													title="Update the campaign"
													class="text-gray-800 btn hover:text-secondary"
													on:click={() => editCampaign(campaign.id)}
												>
													<Icon name="pencil" />
												</button>
											{/if}
											{#if $ACLStore && ACLStore.granted('campaigns.delete')}
												<button
													title="Close this campaign"
													class="text-gray-800 btn hover:text-danger"
													on:click={() => closeCampaign(campaign.id)}
												>
													<Icon name="bin" />
												</button>
											{/if}
										{/if}
									</div>
								</td>
							</tr>
						{:else}
							<tr>
								<td class="px-6 py-4 text-center border-t" colspan="10">No campaigns found.</td>
							</tr>
						{/each}
					</tbody>
				</table>
				{#if numberOfPages > 1}
					<Pagination {links} on:click={changePage} className="my-3" />
				{/if}
			</div>
		</div>
		<!-- End Page -->
	</div>
</div>
